'use client';

import FormError from '@/components/FormError';
import Price from '@/components/Price';
import Rate from '@/components/Rate';
import { Form } from '@/components/ui/form';
import { useAppContext } from '@/context/app';
import { formatString } from '@/lib/format-string';
import { cn } from '@/lib/utils';
import { ConfigurableProduct, ConfigurableProductOptions, Maybe } from '@/types/graphql.prod';
import { findMatchingVariant } from '@/util/findMatchingProductVariant';
import { isProductConfigurable } from '@/util/isProductConfigurable';
import { isSupportedProductType as isSupported } from '@/util/isSupportedProductType';
import { ratingToStarValue } from '@/util/rating-to-star-value';
import { zodResolver } from '@hookform/resolvers/zod';
import Image from 'next/image';
import { ReactNode, Suspense, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';
import { z } from 'zod';
import { useMiniCartStore } from '../MiniCart/use-new-minicart-store';
import ProductImageCarousel from '../product-image-carousel';
import { Button } from '../ui/button';
import TTBF001CP from './TTBF001CP';
import TTBF001CPParams from './TTBF001CPParams';
import TTBF002CP from './TTBF002CP';
import TTBF002CPParams from './TTBF002CPParams';
import { AddCartButton } from './add-cart-button';
import AddToCartLoadingIcon from './add-to-cart-loading-icon';
import { ProductDetailAndShipping } from './product-detail-and-shipping';
import classes from './productFullDetail.module.css';
import { QuantitySelect } from './quantity-select';
import { StandardOptions } from './standard-options';
import { useAddToCart } from './use-add-to-cart';
import { useGetIsOutOfStock } from './use-get-is-out-of-stock';
import { useGetOptionCode } from './use-get-option-code';
import { useGetProductPrice } from './use-get-product-price';
import { useOptionsSpecs } from './use-options-sepcs';
import { useGetMediaGalleryEntries } from './useGetMediaGalleryEntries';

import { calculateShippingDate } from '@/util/calculate-shipping-date';
import { transformLabelToPath } from '@/util/transform-label-to-path';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import ProductReviewFormLazyLoad from '../Product/product-review-form-lazy-load';
import NoSsr from '../no-ssr';
import StandardShippingDate from '../standard-shipping-date';
import InitSelectedOptionByQuery from './init-selected-option-by-query';
import QuantityBundle from './quantity-bundle';
import Ttbf003cp from './ttbf003cp';
import Ttbf003cpParams from './ttbf003cp-params';

const NewNotifyMeFormDialog = dynamic(() => import('./NotifyMeFormDialog/new-notify-me-form-dialog'), { ssr: false });
const ShareLinkDialog = dynamic(() => import('./share-link-dialog'), { ssr: false });

export interface SelectedConfigurableOption {
  uid: string;
  label: string;
  option_attribute_id: string;
  option_selection_value: number;
}

export interface SelectedOptions {
  [key: string]: SelectedConfigurableOption | number | string;
  quantity: number | string;
}

export interface SelectedOptionsWithoutQuantity {
  [key: string]: SelectedConfigurableOption;
}

// Correlate a GQL error message to a field. GQL could return a longer error
// string but it may contain contextual info such as product id. We can use
// parts of the string to check for which field to apply the error.
const ERROR_MESSAGE_TO_FIELD_MAPPING: { [key: string]: string } = {
  'The requested qty is not available': 'quantity',
  'Product that you are trying to add is not available.': 'quantity',
  "The product that was requested doesn't exist.": 'quantity',
};

// Field level error messages for rendering.
const ERROR_FIELD_TO_MESSAGE_MAPPING: { [key: string]: string } = {
  quantity: 'The requested quantity is not available.',
};

export const getIsPreOrderInfo = (product: any, optionCodes: Map<any, any>, optionSelections: Map<any, any>) => {
  const { variants } = product;
  const isConfigurable = isProductConfigurable(product);
  const optionsSelected = Array.from(optionSelections.values()).filter(value => !!value).length > 0;

  // cp 产品
  if (isConfigurable && optionsSelected) {
    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    });

    const { product } = item || {};

    const { presale_product } = product || {};

    const note = (
      <div>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='30'
          height='30'
          fill='none'
          viewBox='0 0 30 30'
          className='inline-block h-auto w-5 lg_w-[30px]'
        >
          <path
            fill='#222'
            d='M0 2.945a.904.904 0 0 1 .904-.904h16.651a.904.904 0 0 1 .904.904v2.634l6.099-.02h.003a.9.9 0 0 1 .775.44l4.535 7.557c.084.141.129.302.129.466v6.944a3.483 3.483 0 0 1-3.48 3.478h-1.418a3.545 3.545 0 0 1-3.54 3.516 3.545 3.545 0 0 1-3.54-3.516h-6.255a3.545 3.545 0 0 1-3.54 3.516 3.545 3.545 0 0 1-3.54-3.516H3.48A3.483 3.483 0 0 1 0 20.966V15.83c0-.021.005-.04.006-.06 0-.02-.006-.04-.006-.06zm1.808.904v10.957h14.837l.006-.377V3.849zm19.754 22.303c.956 0 1.733-.779 1.733-1.734s-.777-1.732-1.733-1.732-1.733.776-1.733 1.732.777 1.734 1.733 1.734m-13.336 0c.956 0 1.734-.779 1.734-1.734s-.778-1.732-1.734-1.732c-.955 0-1.733.776-1.733 1.732s.778 1.734 1.733 1.734m-6.418-5.186c0 .921.75 1.67 1.672 1.67h1.69a3.54 3.54 0 0 1 3.056-1.759 3.54 3.54 0 0 1 3.066 1.776 1 1 0 0 1 .174-.017h7.04a3.54 3.54 0 0 1 3.056-1.759 3.54 3.54 0 0 1 3.055 1.759h1.903c.922 0 1.672-.749 1.672-1.67v-6.693L24.05 7.369l-5.48.018-.112 7.066v1.257a1 1 0 0 1-.033.23.9.9 0 0 1-.893.795H1.808z'
          ></path>
        </svg>
        <span className='ml-2.5'>
          <span className='font-sans text-sm text-title lg_text-base'>Order now, receive by:</span>{' '}
          <span className='font-poppins text-sm font-semibold text-primary lg_text-base'>
            &nbsp;
            {calculateShippingDate(presale_product?.shipping_from, presale_product?.shipping_to)}
          </span>
        </span>
      </div>
    );

    const { status: isPreOrder, label } = presale_product || {};

    if (isPreOrder) {
      return {
        isPreOrder: true,
        note,
        label,
      };
    }

    return {
      isPreOrder: false,
      note: '',
    };
  }

  const { presale_product } = product || {};

  const { status: isPreOrder, note, label } = presale_product || {};

  return {
    isPreOrder,
    note,
    label,
  };
};

export const ProductFullDetail = ({
  product,
  onViewReviews,
  onViewQuestions,
  defaultValues,
  handleColorLabelChange,
  seoColorLabel,
  seoUrlKey,
  breadcrumbs,
  originalUrlKey,
}: {
  product: any;
  onViewReviews: any;
  onViewQuestions: any;
  defaultValues: any;
  handleColorLabelChange: any;
  seoColorLabel: string;
  seoUrlKey: string;
  originalUrlKey: string;
  breadcrumbs: ReactNode;
}) => {
  const { ref, inView } = useInView({
    threshold: 0,
    initialInView: true,
  });
  const [shareLinkProductPathname, setShareLinkProductPathname] = useState(`/${seoUrlKey}.html`);
  const [isInitBySearchParams, setIsInitBySearchParams] = useState(true);

  const createFormSchema = (options: Maybe<Maybe<ConfigurableProductOptions>[]> | undefined) => {
    const schemaFields: Record<string, z.ZodTypeAny> = {};

    schemaFields['quantity'] = z.string().min(1, {
      message: `This is a required field!`,
    });

    if (!options) {
      return z.object(schemaFields);
    }

    options?.forEach(item => {
      if (item?.attribute_code) {
        schemaFields[item?.attribute_code] = z.object(
          {
            uid: z.string().min(1),
            label: z.string(),
            option_attribute_id: z.string(),
            option_selection_value: z.number().optional(),
          },
          {
            message: `This is a required field!`,
          },
        );
      }
    });

    return z.object(schemaFields);
  };

  const FormSchema = createFormSchema(product?.configurable_options);
  const form = useForm({
    resolver: zodResolver(FormSchema as any),
    defaultValues: defaultValues,
  });
  const allCPData = form.watch() as SelectedOptions;
  const allCpDataWithoutQuantity = Object.fromEntries(
    Object.entries(allCPData).filter(([key]) => key !== 'quantity'),
  ) as SelectedOptionsWithoutQuantity;
  const selectedOptionsArray = useMemo(() => {
    return Object.values(allCpDataWithoutQuantity)
      .filter(item => item && item.uid)
      .map(item => item.uid);
  }, [allCpDataWithoutQuantity]);

  const [showShareLinkModal, setShowShareLinkModal] = useState(false);

  const { optionCodes } = useGetOptionCode({
    product,
  });
  const optionSelections: Map<any, any> = useMemo(() => {
    const optionMap = new Map();
    const options = product?.configurable_options || [];

    options?.forEach(item => {
      const attribute_id = item?.attribute_id;

      optionMap.set(attribute_id, undefined);
    });

    // Iterate through each property in the JSON object
    for (const [key, value] of Object.entries(allCpDataWithoutQuantity)) {
      if (typeof value === 'object' && value !== null) {
        const item = allCpDataWithoutQuantity[key];

        optionMap.set(item.option_attribute_id, item.option_selection_value);
      }
    }

    return optionMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCpDataWithoutQuantity]);

  const isPreOrderInfo = useMemo(
    () => getIsPreOrderInfo(product, optionCodes, optionSelections),
    [product, optionCodes, optionSelections],
  );

  const productType = product.__typename;
  const isSupportedProductType = isSupported(productType);
  const [notifyMeFormDialogIsOpen, setNotifyMeFormDialogIsOpen] = useState(false);
  const { productPrice } = useGetProductPrice({
    product,
    optionCodes,
    optionSelections,
  });
  const { mediaGalleryEntries } = useGetMediaGalleryEntries({
    product,
    optionCodes,
    optionSelections,
  });
  const { isOutOfStock, isSoldOut } = useGetIsOutOfStock({
    product,
    optionCodes,
    optionSelections,
  });
  const productDetails = {
    description: product.description,
    shortDescription: product.short_description,
    name: product.name,
    price: productPrice?.final_price,
    sku: product.sku,
    videoUrl: product.video_url,
  };

  const setCartStatus = useMiniCartStore(state => state?.setCartStatus);
  const [{ headerVisibleHeight }] = useAppContext();

  const { handleAddToCart, isAddingProduct, addToCartStatus, addToCartQty, errorMessage } = useAddToCart({
    product,
    mediaGalleryEntries,
    selectedOptionsArray,
    afterSubmit: () => {
      setCartStatus('addtocart');
    },
  });

  const { isPreOrder, note, label } = isPreOrderInfo || {};
  const [showReviewFormDialog, setShowReviewFormDialog] = useState(false);
  const errors = new Map();

  if (errorMessage) {
    Object.keys(ERROR_MESSAGE_TO_FIELD_MAPPING).forEach(key => {
      if (errorMessage.includes(key)) {
        const target = ERROR_MESSAGE_TO_FIELD_MAPPING[key];
        const message = ERROR_FIELD_TO_MESSAGE_MAPPING[target as any];

        errors.set(target, message);
      }
    });

    if (errorMessage.includes('The current user cannot')) {
      errors.set('form', [
        new Error('There was a problem with your cart. Please sign in again and try adding the item once more.'),
      ]);
    }

    if (errorMessage.includes('Variable "$cartId" got invalid value null')) {
      errors.set('form', [
        new Error('There was a problem with your cart. Please refresh the page and try adding the item once more.'),
      ]);
    }

    if (!errors.size) {
      errors.set('form', [new Error('Could not add item to cart. Please check required options and try again.')]);
    }
  }

  const { optionSpecs, handleClick, existingOptionValues } = useOptionsSpecs({
    product,
  });

  const onSubmit = (data: any) => {
    const { quantity } = data;

    handleAddToCart(quantity);
  };

  const carouselRenderImages = useMemo(() => {
    return Array.isArray(mediaGalleryEntries)
      ? mediaGalleryEntries.filter(x => !!x && !x.disabled).sort((a, b) => (a.position || 0) - (b.position || 0))
      : [];
  }, [mediaGalleryEntries]);

  const rating = product.review_count ? (
    <section className='flex items-center'>
      <Rate
        disabled
        initialRating={ratingToStarValue(product.rating_summary)}
      />
      <span
        className='ml-[10px] cursor-pointer font-sans text-sm text-title underline hover_text-primary'
        onClick={onViewReviews}
      >
        {product.review_count} Reviews
      </span>
    </section>
  ) : (
    <section
      className='flex items-center'
      onClick={() => {
        setShowReviewFormDialog(true);
      }}
    >
      <Rate
        disabled
        initialRating={0}
      />
      <span className='ml-[10px] cursor-pointer font-sans text-sm text-title underline hover_text-primary'>
        Be the first to review
      </span>
    </section>
  );

  const questions = product.question_answer?.length ? (
    <span className='flex items-center'>
      <span className='ml-[10px] inline-block h-[20px] w-[1px] bg-[#000]'></span>
      <span
        onClick={onViewQuestions}
        className='ml-[10px] cursor-pointer font-sans text-sm text-title underline hover_text-primary'
      >
        {product.question_answer?.length} Questions
      </span>
    </span>
  ) : null;

  const handleFieldChange = (attributeCode, value) => {
    if (attributeCode === 'color') {
      handleColorLabelChange(value.label);
    }

    const configurableOptions = product.configurable_options || [];

    if (!Array.isArray(configurableOptions) || configurableOptions.length === 0) return;

    const currentFormValues = form.getValues();

    const isAllOptionSelected =
      Array.isArray(configurableOptions) &&
      configurableOptions.length > 0 &&
      configurableOptions.every(x => {
        const attributeCode = x?.attribute_code;

        if (!attributeCode) return false;

        return !!currentFormValues[attributeCode]?.uid;
      });

    if (!isAllOptionSelected) return;

    // 检查选择的变体是否下架
    const tempOptionMap = new Map();
    configurableOptions?.forEach(item => {
      const attribute_id = item?.attribute_id;
      tempOptionMap.set(attribute_id, undefined);
    });

    for (const [key, val] of Object.entries(currentFormValues)) {
      if (typeof val === 'object' && val !== null && key !== 'package_quantity') {
        tempOptionMap.set(val.option_attribute_id, val.option_selection_value);
      }
    }

    const selectedVariant = findMatchingVariant({
      variants: product.variants,
      optionCodes,
      optionSelections: tempOptionMap,
    });

    // 如果找到变体且是下架的，就不改变链接地址
    const isVariantOutOfStock =
      selectedVariant?.product?.stock_status !== 'IN_STOCK' && !selectedVariant?.product?.presale_product?.status;

    if (isVariantOutOfStock) return;

    const seoPrefixParts: string[] = [];
    const seoSuffixParts: string[] = [];

    for (const option of configurableOptions) {
      const attributeCode = option?.attribute_code;
      const label = attributeCode ? currentFormValues[attributeCode]?.label : null;

      if (!label) return;

      if (attributeCode === 'color') {
        seoPrefixParts.unshift(transformLabelToPath(label));
        continue;
      }

      if (attributeCode === 'package_quantity') {
        seoPrefixParts.push(transformLabelToPath(label));
        continue;
      }

      if (attributeCode === 'size') {
        let path = transformLabelToPath(label);

        if (/^\d/.test(path)) {
          path = `${parseFloat(path)}-inch`;
        }

        seoSuffixParts.push(path);
        continue;
      }

      console.error('unhandled attributeCode');
      return;
    }

    const seoPrefix = seoPrefixParts.filter(x => typeof x === 'string' && x.trim()).join('-');
    const seoSuffix = seoSuffixParts.filter(x => typeof x === 'string' && x.trim()).join('-');

    let urlKey = originalUrlKey;

    if (seoPrefix) {
      urlKey = urlKey.replace(/^([^-]*)/, `$1-${seoPrefix}`);
    }

    if (seoSuffix) {
      urlKey = urlKey.replace(/-(?=[^-]*$)/, `-${seoSuffix}-`);
    }

    if (urlKey !== originalUrlKey) {
      const nextPathname = `/${urlKey}.html`;
      window.history.replaceState({ seoPath: true }, '', nextPathname);
      setShareLinkProductPathname(nextPathname);
    }
  };

  const { variantInStockMap, variantImageMap, variantPriceValueMap, isVariantOnPresaleMap } = useMemo(() => {
    const defaultValue = {
      variantInStockMap: {},
      variantImageMap: {},
      variantPriceValueMap: {},
      isVariantOnPresaleMap: {},
    };

    if (!product) return defaultValue;

    return (
      product.variants?.reduce((acc, cur) => {
        const isPresale = !!cur?.product?.presale_product?.status;
        const isInStock = isPresale || cur?.product?.stock_status === 'IN_STOCK';
        const { attributes } = cur;

        if (!Array.isArray(attributes) || attributes.length === 0) return acc;

        const valueIndexs = attributes.map(x => x?.value_index).filter(x => x !== undefined && x !== null);

        for (let i = 0, len = attributes.length; i < len; i++) {
          for (const valueIndexCombines of combine(valueIndexs, i + 1)) {
            const combineStr = valueIndexCombines.sort().join(',');
            const media_gallery = cur?.product?.media_gallery_entries
              ?.filter(x => !!x && !x.disabled)
              ?.sort((a, b) => {
                return (a?.position ?? 0) - (b?.position ?? 0);
              });

            if (Array.isArray(media_gallery) && media_gallery.length > 0) {
              acc.variantImageMap[combineStr] = media_gallery
                .filter(x => !x?.disabled)
                .sort((a, b) => (a?.position || 0) - (b?.position || 0))[0];
            }

            acc.variantPriceValueMap[combineStr] = cur.product?.price_range?.maximum_price?.final_price?.value;

            if (
              isPresale &&
              (!Object.prototype.hasOwnProperty.call(acc.isVariantOnPresaleMap, combineStr) ||
                acc.isVariantOnPresaleMap[combineStr] === 1)
            ) {
              acc.isVariantOnPresaleMap[combineStr] = 1;
            } else {
              acc.isVariantOnPresaleMap[combineStr] = 0;
            }

            if (isInStock) {
              acc.variantInStockMap[combineStr] = 1;
              continue;
            }

            if (!Object.prototype.hasOwnProperty.call(acc.variantInStockMap, combineStr)) {
              acc.variantInStockMap[combineStr] = 0;
            }
          }
        }

        return acc;
      }, defaultValue) || defaultValue
    );
  }, [product]);

  return (
    <div className='z-1 relative lg_pt-[20px]'>
      <ShareLinkDialog
        open={showShareLinkModal}
        setOpen={setShowShareLinkModal}
        productPathname={shareLinkProductPathname}
      />
      <div className='mx-auto mb-4 hidden w-[1440px] max-w-full lg_block'>{breadcrumbs}</div>
      <div className='mx-auto flex w-[1440px] max-w-full flex-col px-[17px] pt-[20px] lg_flex-row lg_items-start lg_gap-[40px] lg_px-0 lg_pb-[20px] lg_pt-0'>
        <div className='mb-[10px] block lg_hidden'>
          <h1
            aria-live='polite'
            className='mb-[10px] font-poppins text-[18px] font-[600] leading-[1.4] text-title'
            data-cy='ProductFullDetail-productName'
          >
            {formatString(productDetails?.name ?? '')}
          </h1>
          <div className='flex items-center'>
            {rating}
            {questions}
          </div>
        </div>
        <section
          className='w-full lg_sticky lg_w-[830px]'
          style={{ top: headerVisibleHeight }}
        >
          <ProductImageCarousel
            gallery={carouselRenderImages}
            videoUrl={productDetails?.videoUrl ?? ''}
            productName={formatString(productDetails?.name || '')}
            seoColorLabel={seoColorLabel}
          />
        </section>
        <div className='sticky top-0 z-[2] w-full lg_flex-1'>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit, errors => {
                // 处理错误，滚动到第一个错误字段
                const errorElement = document.querySelector(`#form-error`);

                if (errorElement) {
                  // 平滑滚动到错误元素
                  errorElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                }
              })}
            >
              <Suspense>
                <InitSelectedOptionByQuery
                  configurableOptions={product.configurable_options}
                  setIsInitBySearchParams={setIsInitBySearchParams}
                />
              </Suspense>
              <div className=''>
                <section className={cn('flex flex-col', 'px-0')}>
                  <div className='hidden items-start gap-x-2.5 lg_flex'>
                    <h1
                      aria-live='polite'
                      className='mb-[20px] font-poppins text-[20px] font-[600] leading-[1.4] text-title'
                      data-cy='ProductFullDetail-productName'
                    >
                      {formatString(productDetails?.name ?? '')}
                    </h1>
                    <button
                      type='button'
                      className='flex-none lg_hidden'
                      onClick={() => {
                        setShowShareLinkModal(true);
                      }}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='25'
                        height='25'
                        fill='none'
                        viewBox='0 0 25 25'
                      >
                        <path
                          fill='#2F6132'
                          d='M20.955 14.375a.875.875 0 0 0-.875.875v2.025a3.18 3.18 0 0 1-3.176 3.176H8.103a3.18 3.18 0 0 1-3.175-3.176V7.632a3.18 3.18 0 0 1 3.175-3.176h1.6a.875.875 0 0 0 0-1.75h-1.6a4.93 4.93 0 0 0-4.925 4.926v9.64A4.93 4.93 0 0 0 8.103 22.2h8.801a4.93 4.93 0 0 0 4.926-4.927V15.25a.875.875 0 0 0-.875-.875'
                        />
                        <path
                          fill='#2F6132'
                          d='M13.28 12.389a.88.88 0 0 0 .625-.266l5.92-6.125V8.84a.874.874 0 1 0 1.75 0V4.966a2.136 2.136 0 0 0-2.133-2.133h-3.58a.875.875 0 1 0 0 1.75h2.902l-6.112 6.325a.875.875 0 0 0 .625 1.482z'
                        />
                      </svg>
                    </button>
                  </div>

                  <div className='hidden items-center justify-between lg_flex lg_flex-col lg_items-stretch lg_justify-start'>
                    <div className='mb-[20px] flex items-center'>
                      {rating}
                      {questions}
                    </div>

                    <div
                      className={clsx(
                        'font-poppins text-[24px] font-[600] leading-[1.4] text-title',
                        isInitBySearchParams && 'invisible',
                      )}
                    >
                      {isSoldOut ? (
                        'Retired'
                      ) : (
                        <>
                          <Price
                            currencyCode={productDetails.price.currency}
                            value={productDetails.price.value}
                          />
                          <span className='font-normal text-paragraph line-through '>
                            {product?.price_range?.maximum_price?.discount?.amount_off &&
                            product?.price_range?.maximum_price?.discount?.amount_off > 0 ? (
                              <Price
                                currencyCode={product?.price?.regularPrice?.amount?.currency}
                                value={product?.price?.regularPrice?.amount?.value}
                              />
                            ) : null}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className='mt-[20px] flex items-center justify-between lg_hidden'>
                    <div
                      className={clsx(
                        'font-poppins text-[24px] font-[600] leading-[1.4] text-title',
                        isInitBySearchParams && 'invisible',
                      )}
                    >
                      {isSoldOut ? (
                        'Retired'
                      ) : (
                        <>
                          <Price
                            currencyCode={productDetails.price.currency}
                            value={productDetails.price.value}
                          />
                          <span className='font-normal text-paragraph line-through '>
                            {product?.price_range?.maximum_price?.discount?.amount_off &&
                            product?.price_range?.maximum_price?.discount?.amount_off > 0 ? (
                              <Price
                                currencyCode={product?.price?.regularPrice?.amount?.currency}
                                value={product?.price?.regularPrice?.amount?.value}
                              />
                            ) : null}
                          </span>
                        </>
                      )}
                    </div>
                    <div
                      className={classes.shareIcon}
                      onClick={() => {
                        setShowShareLinkModal(true);
                      }}
                    ></div>
                  </div>
                </section>

                <FormError errors={errors.get('form') || []} />
                <section className={'pt-[10px] lg_pt-[40px]'}>
                  <StandardOptions
                    configurableOptions={product.configurable_options}
                    variants={product.variants}
                    product={product}
                    optionSpecs={optionSpecs}
                    handleClick={handleClick}
                    onFieldChange={handleFieldChange}
                    isVariantOnPresaleMap={isVariantOnPresaleMap}
                    variantInStockMap={variantInStockMap}
                    existingOptionValues={existingOptionValues}
                  />
                </section>

                {isOutOfStock ? (
                  <div
                    className={clsx(
                      'mt-[40px] flex items-center font-poppins font-[600] ',
                      isSoldOut ? 'text-red' : 'text-[#DBA802]',
                    )}
                  >
                    <span className={classes.warningIcon}></span>
                    {isSoldOut ? 'The item has been retired' : 'The item is currently out of stock'}
                  </div>
                ) : isPreOrder ? (
                  <div className='mt-10'>{note}</div>
                ) : (
                  <NoSsr fallback={<div className='mt-10 h-[21px] lg_h-[30px]'></div>}>
                    <StandardShippingDate
                      renderContent={estDeliveryText => {
                        if (!estDeliveryText) return <div className='invisible mt-[40px] h-[21px] lg_h-[30px]'></div>;

                        return (
                          <div className='mt-[40px] flex items-center'>
                            <span className={classes.shippingIcon}></span>
                            <span className='ml-[10px] font-sans text-sm leading-[1.4] text-title lg_text-[16px]'>
                              Get it by:&nbsp;
                              <span className='font-poppins font-[600] text-primary'>{estDeliveryText}</span>
                            </span>
                          </div>
                        );
                      }}
                    />
                  </NoSsr>
                )}

                <div
                  ref={ref}
                  className='mt-[20px] flex items-center justify-between gap-[10px]'
                >
                  {isOutOfStock ? null : (
                    <div className='w-[100px]'>
                      <QuantitySelect
                        showQuantity={false}
                        form={form}
                      />
                    </div>
                  )}
                  <div className='flex-1'>
                    <AddCartButton
                      isPreOrder={isPreOrder}
                      isAddingProduct={isAddingProduct}
                      addToCartStatus={addToCartStatus}
                      label={label}
                      isOutOfStock={isOutOfStock}
                      isSoldOut={isSoldOut}
                      setNotifyMeFormDialogIsOpen={setNotifyMeFormDialogIsOpen}
                      note={note}
                      isSupportedProductType={isSupportedProductType}
                    />
                  </div>
                </div>

                {Array.isArray(product.qty_bundle_rules) &&
                product.qty_bundle_rules.length > 0 &&
                product.qty_bundle_rules.reduce((acc, cur) => {
                  acc.add(cur?.type);
                  return acc;
                }, new Set()).size === 1 ? (
                  <QuantityBundle
                    currencyCode={productDetails.price.currency}
                    product={product}
                    isOutOfStock={isOutOfStock}
                    variantInStockMap={variantInStockMap}
                    variantImageMap={variantImageMap}
                    variantPriceValueMap={variantPriceValueMap}
                  />
                ) : null}

                <div className='mt-[20px] flex items-center gap-[12px] rounded-[10px] bg-[#F7F7F5] px-[5px] py-[10px] lg_gap-[20px] lg_p-[20px]'>
                  <span className={classes.daysIcon}></span>
                  <div className='flex flex-col items-start gap-[5px] lg_gap-[10px]'>
                    <p className='lg_text-normal font-poppins text-sm font-[600] leading-[1.2] text-title'>
                      Free Shipping & 30-Day Free Returns
                    </p>
                    <p className='font-sans text-sm text-title'>
                      Shop with confidence:free shipping and risk-free 30-day returns.{' '}
                    </p>
                  </div>
                </div>
                <div className='mt-[40px]'>
                  <ProductDetailAndShipping
                    productDetails={productDetails}
                    product={product}
                  />
                </div>

                {
                  // 底部加车按钮
                }
                <NoSsr>
                  <div
                    className={cn(
                      'fixed bottom-0 left-0 z-30 w-full bg-white shadow-[0_-4px_5px_rgba(217,217,217,0.35)] transition-opacity lg_hidden',
                      inView ? 'hidden opacity-0' : 'block opacity-100',
                    )}
                  >
                    <div className='flex justify-between gap-4 p-[15px]'>
                      {isOutOfStock ? null : (
                        <div className='w-[100px]'>
                          <QuantitySelect
                            className='h-[50px]'
                            showQuantity={false}
                            form={form}
                          />
                        </div>
                      )}

                      <AddCartButton
                        isPreOrder={isPreOrder}
                        isAddingProduct={isAddingProduct}
                        addToCartStatus={addToCartStatus}
                        label={label}
                        isOutOfStock={isOutOfStock}
                        isSoldOut={isSoldOut}
                        setNotifyMeFormDialogIsOpen={setNotifyMeFormDialogIsOpen}
                        isSupportedProductType={isSupportedProductType}
                        note={note}
                        showReadyToShip={false}
                        className='h-[50px]'
                        outerClassName='flex-1'
                        showPreOrderTips={false}
                        showNotifyMeTips={false}
                      />
                    </div>
                  </div>
                </NoSsr>
              </div>

              <NoSsr>
                <PCAddToCartFixed
                  product={product}
                  mediaGalleryEntries={mediaGalleryEntries}
                  isAddingProduct={isAddingProduct}
                  isOutOfStock={isOutOfStock}
                  isSoldOut={isSoldOut}
                  isPreOrder={isPreOrder ?? false}
                  note={note}
                  setNotifyMeFormDialogIsOpen={setNotifyMeFormDialogIsOpen}
                  productDetails={productDetails}
                  className={cn(inView ? 'hidden opacity-0' : 'hidden opacity-100  lg_block')}
                  addToCartStatus={addToCartStatus}
                />
              </NoSsr>
            </form>
          </Form>
        </div>
      </div>

      {
        // Titto 喂鸟器相关
        // 001CP
      }

      {product.id === 1034 ? <TTBF001CP /> : null}

      {product.id === 1035 ? <TTBF002CP /> : null}

      {
        // 002CP
        // titto-2-in-1-smart-bird-feeder.
      }

      {product.id === 1034 ? <TTBF001CPParams /> : null}

      {product.id === 1035 ? <TTBF002CPParams /> : null}

      {product.id === 1119 ? <Ttbf003cp /> : null}

      {product.id === 1119 ? <Ttbf003cpParams /> : null}

      <NewNotifyMeFormDialog
        isOpen={notifyMeFormDialogIsOpen}
        setNotifyMeFormDialogIsOpen={setNotifyMeFormDialogIsOpen}
        product={product}
        variants={product.variants}
        configurable_options={product.configurable_options}
        optionSelections={optionSelections}
      />
      <ProductReviewFormLazyLoad
        product={product}
        open={showReviewFormDialog}
        onOpenChange={setShowReviewFormDialog}
      />
    </div>
  );
};

function PCAddToCartFixed({
  product,
  mediaGalleryEntries,
  isAddingProduct,
  isOutOfStock,
  isSoldOut,
  isPreOrder,
  note,
  setNotifyMeFormDialogIsOpen,
  productDetails,
  className,
  addToCartStatus,
}: {
  product: ConfigurableProduct;
  mediaGalleryEntries: any;
  isAddingProduct: boolean;
  isOutOfStock: boolean;
  isSoldOut: boolean;
  isPreOrder: boolean;
  note: string;
  setNotifyMeFormDialogIsOpen: any;
  productDetails: any;
  addToCartStatus: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 z-50 hidden h-[100px] w-screen bg-white shadow-[0_-2px_10px_rgba(175,175,175,0.4)] transition-all duration-lg',
        className,
      )}
    >
      <div className='mx-auto h-full w-full max-w-[1440px] items-center justify-between lg_flex'>
        <div className='flex items-center gap-[30px]'>
          <Image
            className='h-[80px] w-[80px]'
            src={mediaGalleryEntries?.[0]?.url}
            alt={product?.name ?? ''}
            width={80}
            height={80}
          />
          <span className='line-clamp-2 max-w-[380px] overflow-hidden font-poppins text-[18px] leading-[1.4] text-title'>
            {product?.name}
          </span>
          <span className='font-poppins text-[22px] font-[600] text-primary'>
            {isSoldOut ? (
              'Retired'
            ) : (
              <Price
                currencyCode={productDetails.price.currency}
                value={productDetails.price.value}
              />
            )}
          </span>
        </div>

        <div>
          {isOutOfStock || isSoldOut ? (
            <span
              className={clsx('flex items-center font-poppins font-[600] ', isSoldOut ? 'text-red' : 'text-[#DBA802]')}
            >
              <span className={classes.warningIcon}></span>
              {isSoldOut ? 'The item has been retired' : 'The item is currently out of stock'}
            </span>
          ) : isPreOrder ? (
            <>{note}</>
          ) : (
            <StandardShippingDate
              renderContent={estDeliveryText => {
                if (!estDeliveryText) return <span></span>;

                return (
                  <span className='font-poppins font-[600] text-secondary'>
                    <span>Receive by: &nbsp;</span>
                    <span>{estDeliveryText}</span>
                  </span>
                );
              }}
            />
          )}
        </div>

        {isOutOfStock || isSoldOut ? (
          <>
            <Button
              type='button'
              disabled={isSoldOut}
              className={cn(
                'h-[54px] w-[400px] max-w-full rounded-[10px] border-1 border-primary bg-white font-poppins font-[600] text-primary',
                !isSoldOut && 'hover_text-white',
                isSoldOut && 'disabled_opacity-100',
              )}
              onClick={() => {
                if (!isSoldOut) setNotifyMeFormDialogIsOpen(true);
              }}
            >
              {isSoldOut ? 'NO LONGER AVAILABLE' : 'Notify Me'}
            </Button>
          </>
        ) : (
          <Button
            className='2font-poppins h-[54px] w-[400px] rounded-[10px] bg-primary font-[600] text-white hover_opacity-90'
            disabled={isAddingProduct || addToCartStatus}
          >
            {addToCartStatus ? (
              <div className='relative flex h-full w-full items-center justify-center px-5'>
                <span>ADDED</span>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  fill='none'
                  viewBox='0 0 16 16'
                  className='absolute right-0 top-1/2 block -translate-y-1/2'
                >
                  <path
                    stroke='#fff'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M15 2 5.2 14 1 9.5'
                  />
                </svg>
              </div>
            ) : isAddingProduct ? (
              <AddToCartLoadingIcon />
            ) : isPreOrder ? (
              'PRE ORDER'
            ) : (
              'ADD TO CART'
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

const combine = (elements, count) => {
  if (count > elements.length || count <= 0) return [];

  if (count === elements.length) return [elements];

  if (count === 1) return elements.map(element => [element]);

  const result: any[] = [];
  const stack: any[] = [
    {
      start: 0,
      comb: [],
      depth: 0,
    },
  ];
  const elementCount = elements.length;

  while (stack.length > 0) {
    const { start, comb, depth } = stack.pop();

    if (depth === count) {
      result.push(comb);
      continue;
    }

    for (let i = start; i <= elementCount - (count - depth); i++) {
      stack.push({
        start: i + 1,
        comb: comb.concat(elements[i]),
        depth: depth + 1,
      });
    }
  }

  return result;
};
